import Echo from 'laravel-echo';
import { type Channel } from 'laravel-echo/dist/channel';
import socketIdClient from 'socket.io-client';
import { CONFIG } from '~/_shared/constants/config';
import type { PdfReportStatusCode } from '~/exportReport/pdfReport.repository';
import type {
  ItemizedExportId, ItemizedExportStatus,
} from '~/spreadsheet/itemizeExport.repository';
import type { GetBulkDataAsyncId } from '~/spreadsheet/spreadsheet.repository';
import type { MapQueryStatusResponse } from '~/store/boundaryItems/boundaryItems.repository';
import { type BoundaryTerritoryGroupResponse } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import type {
  V4MapMigrationErrorMessage, V4MapMigrationProgressMessage,
} from '~/store/mapMigration/types/v4MapMigrationMessage.type';
import type { RowRelationsEvent } from '~/store/rowRelations/rowRelations.types';
import {
  type BoundaryCreateOptimizedStatusResponse, type BoundaryCreateWMSOptimizedStatusResponse,
} from '../../../map/map.repository';
import { notNullsy } from '../typeGuards';
import { getPrefixedApiUrl } from './api.helpers';

type SpreadsheetImportBroadcastData = {
  message: string;
  socket: null;
  spreadsheetId: number;
  userId: number;
};

export type GeocodingStartedEventBroadcastData = {
  clientId: number;
  message: string;
  percentage_progress: number;
  realSpreadsheetId: number;
  userId: number;
  virtualSpreadsheets: number[];
};

export type GeocodingEventAdditionalFlags = {
  geocoding_limit_exceeded: boolean;
  travel_time_geocoding_limit_exceeded: boolean;
};

export type GeocodingEventBroadcastData = {
  client_id: number;
  virtual_id: number;
  real_spreadsheet_id: number;
  snapshot: number;
  version: number;
  user_id: number;
  percentage: number;
  counters: {
    completed: string; // percentage with % symbol
    total: number; // total count of records
    processed: {
      matches: number; // count of geopoints received from matched lat and lng
      cache: number; // count of geopoints received from cache
      api: number; // count of geopoints received from api
      failed: number; // count of failed geocoding requests
    };
  };
  additional: GeocodingEventAdditionalFlags;
};

export type CreateReportEventBroadcastData = {
  client_id: number;
  code: PdfReportStatusCode;
  hash: string;
  map_id: number;
  path: string;
  mess: string;
};

export type ConvertStartedEventBroadcastData = {
  approximate_time: string;
  boundary_group_id: number;
  boundary_territory_type: string;
  client_id: number;
  map_id: number;
};

export type ConvertEventBroadcastData = {
  btg_id: number | null;
  map_id: number;
  success: boolean;
};

let broadcast: Echo | undefined | null;
let userBroadcast: Channel | null = null;

type BroadcastInitParams = {
  userId: number;
};

export const initializeBroadcast = (params: BroadcastInitParams) => {
  const url = CONFIG.SOCKET_URL || getPrefixedApiUrl('');

  broadcast = new Echo({
    client: socketIdClient,
    broadcaster: 'socket.io',
    host: url,
  });

  userBroadcast = broadcast.private(`user.${params.userId}`);
};

export const destroyBroadcast = () => {
  if (!broadcast) {
    return;
  }

  broadcast.disconnect();
  broadcast = null;
};

export const getUserSpreadsheetImportBroadcast = (listener: (e: SpreadsheetImportBroadcastData) => void, onError: () => void) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('SpreadsheetImport')
    .listen('SpreadsheetImport', listener)
    .error(onError);
};

interface GeocodingEventBroadcastListenerParams {
  onStarted: (e: GeocodingStartedEventBroadcastData) => void;
  onEvent: (e: GeocodingEventBroadcastData) => void;
  onError: () => void;
}

export const registerGeocodingEventBroadcastListener = ({ onStarted, onEvent, onError }: GeocodingEventBroadcastListenerParams) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('GeocodingEvent')
    .listen('GeocodingEvent', onEvent)
    .stopListening('GeocodingStarted')
    .listen('GeocodingStarted', onStarted)
    .error(onError);
};

interface RowRelationsEventBroadcastListenerParams {
  onEvent: (e: RowRelationsEvent) => void;
  onError: () => void;
}

export const registerRowRelationsEventBroadcastListener = ({
  onEvent,
  onError,
}: RowRelationsEventBroadcastListenerParams) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('Intersections\\RowRelations')
    .listen('Intersections\\RowRelations', onEvent)
    .error(onError);
};

type MigrationListeners = {
  readonly onEvent: (message: V4MapMigrationProgressMessage) => void;
  readonly onError: (message?: V4MapMigrationErrorMessage) => void;
};

export const registerMigrationEventBroadcastListener = ({ onEvent, onError }: MigrationListeners) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('DataMigrated')
    .listen('DataMigrated', onEvent)
    .stopListening('DataMigrationFailed')
    .listen('DataMigrationFailed', onError)
    .error(onError);
};

export type WMSOptimizedTerritoriesStatusEventData = {
  success: boolean;
  status: BoundaryCreateWMSOptimizedStatusResponse;
  id: number;
};

export type WMSOptimizedTerritoriesEventData = {
  message: string;
  id: number;
  btg: BoundaryTerritoryGroupResponse;
};

export type WMSFailedOptimizedTerritoriesEventData = {
  message: string;
  id: number;
};

export type WMSCanceledOptimizedTerritoriesEventData = {
  message: string;
  id: number;
};

type WMSOptimizedTerritoriesEventBroadcastListenerCallbacks = {
  onSuccess: (e: WMSOptimizedTerritoriesEventData) => void;
  onError: (e: WMSFailedOptimizedTerritoriesEventData) => void;
  onStatusChange: (e: WMSOptimizedTerritoriesStatusEventData) => void;
  onCancel: (e: WMSCanceledOptimizedTerritoriesEventData) => void;
};

export const registerWMSOptimizedTerritoriesBroadcastListener = ({ onSuccess, onStatusChange, onError, onCancel }: WMSOptimizedTerritoriesEventBroadcastListenerCallbacks) => {
  if (!userBroadcast) {
    return;
  }

  return userBroadcast
    .stopListening('OptimizedTerritories\\ProcessingEvent')
    .listen('OptimizedTerritories\\ProcessingEvent', onStatusChange)
    .stopListening('OptimizedTerritories\\FailedEvent')
    .listen('OptimizedTerritories\\FailedEvent', onError)
    .stopListening('OptimizedTerritories\\FinishedEvent')
    .listen('OptimizedTerritories\\FinishedEvent', onSuccess)
    .stopListening('OptimizedTerritories\\CanceledEvent')
    .listen('OptimizedTerritories\\CanceledEvent', onCancel);
};

export type OptimizedTerritoriesStatusEventData = {
  success: boolean;
  status: BoundaryCreateOptimizedStatusResponse;
  hash: string;
};

type OptimizedTerritoriesExportConfigEventData = {
  message: string;
  hash: string;
  btg: { url: string };
};

export type OptimizedTerritoriesBTGEventData = {
  message: string;
  hash: string;
  btg: BoundaryTerritoryGroupResponse;
};

export type OptimizedTerritoriesEventData = OptimizedTerritoriesBTGEventData | OptimizedTerritoriesExportConfigEventData;

export const isOptimizedTerritoriesExportConfigEventData = (
  message: OptimizedTerritoriesEventData,
): message is OptimizedTerritoriesExportConfigEventData => {
  return notNullsy((message as OptimizedTerritoriesExportConfigEventData).btg.url);
};

export type FailedOptimizedTerritoriesEventData = {
  message: string;
  hash: string;
  data?: {
    message: string;
    details?: string;
  };
};

const isFailedOptimizedTerritoriesEventData = (
  event: OptimizedTerritoriesEventData | FailedOptimizedTerritoriesEventData,
): event is FailedOptimizedTerritoriesEventData => event.message === 'FAILED';

export type CanceledOptimizedTerritoriesEventData = {
  message: string;
  hash: string;
};

type OptimizedTerritoriesEventBroadcastListenerCallbacks = {
  onSuccess: (e: OptimizedTerritoriesEventData | OptimizedTerritoriesExportConfigEventData) => void;
  onError: (e: FailedOptimizedTerritoriesEventData) => void;
  onStatusChange: (e: OptimizedTerritoriesStatusEventData) => void;
  onCancel: (e: CanceledOptimizedTerritoriesEventData) => void;
};

export const registerOptimizedTerritoriesBroadcastListener = ({ onSuccess, onStatusChange, onError, onCancel }: OptimizedTerritoriesEventBroadcastListenerCallbacks) => {
  if (!userBroadcast) {
    return;
  }

  return userBroadcast
    .stopListening('MaptiveOptimizedTerritories\\ProcessingEvent')
    .listen('MaptiveOptimizedTerritories\\ProcessingEvent', onStatusChange)
    .stopListening('MaptiveOptimizedTerritories\\FailedEvent')
    .listen('MaptiveOptimizedTerritories\\FailedEvent', onError)
    .stopListening('MaptiveOptimizedTerritories\\FinishedEvent')
    .listen('MaptiveOptimizedTerritories\\FinishedEvent', (event: OptimizedTerritoriesEventData | FailedOptimizedTerritoriesEventData) => {
      if (isFailedOptimizedTerritoriesEventData(event)) {
        onError(event);
      }
      else {
        onSuccess(event);
      }
    })
    .stopListening('MaptiveOptimizedTerritories\\CanceledEvent')
    .listen('MaptiveOptimizedTerritories\\CanceledEvent', onCancel);
};

type TeamMemberStatusEventData = Readonly<{
  client_id: number;
  disabled: Readonly<{
    id: number;
    name: string;
  }>;
}>;

export const registerMemberDisabledBroadcastListener = (onEvent: (e: TeamMemberStatusEventData) => void) => {
  if (!userBroadcast) {
    return;
  }

  userBroadcast
    .stopListening('DeactivatedUserEvent')
    .listen('DeactivatedUserEvent', onEvent);
};

type ItemizedExportBroadcastData = Readonly<{
  status: ItemizedExportStatus;
  hash: ItemizedExportId;
}>;

export const getItemizedExportBroadcast = (listener: (e: ItemizedExportBroadcastData) => void, onError: () => void) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('SpreadsheetItemizedRowsExportEvent')
    .listen('SpreadsheetItemizedRowsExportEvent', listener)
    .error(onError);
};

export enum SpreadsheetDataExportEventStatus {
  Success = 'success',
  Pending = 'pending',
  Failed = 'failed',
}

type BulkSpreadsheetDataBroadcastData = Readonly<{
  status: SpreadsheetDataExportEventStatus;
  hash: GetBulkDataAsyncId;
}>;

export const getBulkSpreadsheetDataBroadcast = (listener: (e: BulkSpreadsheetDataBroadcastData) => void, onError: () => void) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('SpreadsheetDataExportEvent')
    .listen('SpreadsheetDataExportEvent', listener)
    .error(onError);
};

export enum GetDriveTimeEventStatus {
  Processed = 'PROCESSED',
  Failed = 'FAILED',
}

export type GetDriveTimeEvent = Readonly<{
  status: GetDriveTimeEventStatus;
  hash: string;
}>;

export const registerGetDriveTimeEventBroadcastListener = (listener: (e: GetDriveTimeEvent) => void, onError: () => void) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('TravelTime\\TimeMapProcessedEvent')
    .listen('TravelTime\\TimeMapProcessedEvent', listener)
    .error(onError);
};

export type EntityAccessInvitationsEvent = Readonly<{
  map_id: number;
}>;

export const registerEntityAccessInvitationsEventsBroadcastListener = (listener: (e: EntityAccessInvitationsEvent) => void, onError: () => void) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('EntityAccesses\\InvitationEvent')
    .listen('EntityAccesses\\InvitationEvent', listener)
    .error(onError);
};

export type StatsEvent = Readonly<{
  id: number;
}>;

export const registerStatsEventsBroadcastListener = ({ onError, onStarted, onFinished }: {
  onStarted: (e: StatsEvent) => void;
  onFinished: (e: StatsEvent) => void;
  onError: () => void;
}) => {
  if (!userBroadcast) {
    onError();
    return;
  }

  return userBroadcast
    .stopListening('Charts\\BoundaryStatisticStarted')
    .listen('Charts\\BoundaryStatisticStarted', onStarted)
    .stopListening('Charts\\BoundaryStatisticFinished')
    .listen('Charts\\BoundaryStatisticFinished', onFinished)
    .stopListening('Charts\\BoundaryStatisticFailed')
    .listen('Charts\\BoundaryStatisticFailed', onError)
    .error(onError);
};

export const registerCreateReportEventsBroadcastListener = ({ onError, onFinished }: {
  onFinished: (e: CreateReportEventBroadcastData) => void;
  onError: (error: Error) => void;
}) => {
  if (!userBroadcast) {
    onError(new Error('UserBroadcast not set'));
    return;
  }

  return userBroadcast
    .stopListening('ScreenshotEvent')
    .listen('ScreenshotEvent', onFinished)
    .error(onError);
};

export type AreaQuerySuccessEventData = Readonly<{
  hash: string;
  message: MapQueryStatusResponse;
}>;

export type AreaQueryFailedEventData = Readonly<{
  reason: string;
  hash: string;
}>;

export type AreaQueryEventData = Readonly<{
  hash: string;
  message?: MapQueryStatusResponse;
  reason?: string;
}> & (AreaQuerySuccessEventData | AreaQueryFailedEventData);

export const registerAreaQueryBroadcastListener = (
  listener: (e: AreaQuerySuccessEventData) => void,
  onError: (e: AreaQueryFailedEventData) => void,
) => {
  if (!userBroadcast) {
    return;
  }

  return userBroadcast
    .stopListening('Boundaries\\AreaQueryEvent')
    .listen('Boundaries\\AreaQueryEvent', listener)
    .stopListening('Boundaries\\AreaQueryFailedEvent')
    .listen('Boundaries\\AreaQueryFailedEvent', onError);
};
